// Test change
import React, { useEffect, useState } from 'react';
import { read, utils } from 'xlsx';
import { useNavigate, useLocation } from 'react-router-dom';
import './ResultsPage.css';

// Footer constants
const FOOTER_CONTENT = {
  copyright: "© The BTL Calculator Company Ltd. All Rights Reserved 2025",
  professionalUse: "Not for public use. For professional intermediaries use only.",
  productDisclaimer: "Product disclaimer: The Buy to Let Calculator is designed for professional use by mortgage intermediaries for guidance & information purposes only. Any output of the system shall not constitute a commitment to lend. Other underwriting factors and the selection of mortgage product could influence the 'Maximum Loan' amount that could be borrowed and it is the responsibility of the professional mortgage intermediary to consider these additional factors.",
  tapExplanation: "* TAP (Total Amount Payable) is purely based on the Required Loan Amount as input, and is calculated by multiplying the monthly payment (with or without the product/arrangement/booking fee being added) by the monthly promotional period of the product (e.g. 24 for 2 year fixed as well as tracker products, 36 for 3 year products and 60 for 5 year product etc.).  If the fee is being added to the loan amount, the interest charged on the fee is also taken into account.  Please note that it is the responsibility of the mortgage intermediary to consider any other fees, such as, for example, valuation and application fees, that may be payable and advise the consumer accordingly.",
  productExclusions: "The calculator does not include any FAMILY LET/BUY FOR UNI/LET TO BUY/EXPAT or specific CONSUMER BUY TO LET ONLY Products and nor does it cater for any clients with missed payments, CCJ's, defaults and bankruptcy issues.",
  greenDefinition: "Definition of Green varies from lender to lender so you should carefully check the lenders' underwriting criteria before proceeding.",
  remortgageDisclaimer: "With all REMORTGAGE products, the products listed assume the user is requesting a free valuation & free standard legal services package (or cashback) if available from the relevant lender (or CashBack if they don't offer a free legal service). You should complete your own research if you wish to analyse any other products that may be available from the same lender on a remortgage basis.",
  liability: "The BTLCC assumes no responsibility or liability for any errors or omissions in the product information provided by the Lenders, which is displayed on this site an \"as is\" basis with no guarantees of completeness, accuracy, usefulness or timeliness. Please note that Lenders may launch a new product or withdraw a product at any time without prior notice. Please check directly with the Lender regarding product availability and criteria."
};

const FOOTER_STYLES = {
  container: {
    marginTop: '30px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '3px solid #000000',
    fontFamily: 'Century Gothic, sans-serif',
    textAlign: 'center'
  },
  text: {
    fontSize: '12px',
    lineHeight: '1.4',
    color: '#0000FF',
    fontWeight: 'bold',
    marginBottom: '10px'
  },
  lastText: {
    fontSize: '12px',
    lineHeight: '1.4',
    color: '#0000FF',
    fontWeight: 'bold'
  }
};

// Footer component
const Footer = () => {
  return (
    <div style={FOOTER_STYLES.container}>
      <div style={FOOTER_STYLES.text}>
        {FOOTER_CONTENT.copyright}
      </div>
      <div style={FOOTER_STYLES.text}>
        {FOOTER_CONTENT.professionalUse}
      </div>
      <div style={FOOTER_STYLES.text}>
        {FOOTER_CONTENT.productDisclaimer}
      </div>
      <div style={FOOTER_STYLES.text}>
        {FOOTER_CONTENT.tapExplanation}
      </div>
      <div style={FOOTER_STYLES.text}>
        {FOOTER_CONTENT.productExclusions}
      </div>
      <div style={FOOTER_STYLES.text}>
        {FOOTER_CONTENT.greenDefinition}
      </div>
      <div style={FOOTER_STYLES.text}>
        {FOOTER_CONTENT.remortgageDisclaimer}
      </div>
      <div style={FOOTER_STYLES.lastText}>
        {FOOTER_CONTENT.liability}
      </div>
    </div>
  );
};

function ResultsPage() {
  const location = useLocation();
  const formData = location.state?.formData;
  const [results, setResults] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'totalCostWithoutFeeInterest', direction: 'asc' });
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [selectedLenders, setSelectedLenders] = useState([]);
  const [isLenderModalOpen, setIsLenderModalOpen] = useState(false);
  const [tempSelectedLenders, setTempSelectedLenders] = useState([]);
  const [excludeGreenProducts, setExcludeGreenProducts] = useState(false);
  const [showAffordableOnly, setShowAffordableOnly] = useState(false);
  const navigate = useNavigate();

  const cleanNumber = (val) => parseFloat(val?.toString().replace(/,/g, '')) || 0;

  // Function to get lender website URL
  const getLenderWebsiteURL = (lenderName) => {
    const lenderURLs = {
      'Accord': 'https://www.accordmortgages.com/btl/products',
      'Aldermore': 'https://www.aldermore.co.uk/intermediaries/mortgages/buy-to-let/',
      'Bank of Ireland': 'https://www.bankofireland4intermediaries.co.uk/new-business-products/',
      'Bath BS': 'https://www.bathbuildingsociety.co.uk/intermediaries/browse-mortgages-by-product-type/buy-to-let/',
      'BM Solutions': 'https://www.bmsolutions.co.uk/',
      'Bucks BS': 'https://www.bucksbs.co.uk/intermediaries/products/',
      'Cambs BS': 'https://www.cambridgeforintermediaries.co.uk/products',
      'Castle Trust': 'https://www.castletrust.co.uk/intermediaries-lending/products/list-of-all-products/buy-to-let',
      'CHL 1': 'https://www.chlmortgages.co.uk/intermediaries/products/standard/',
      'Chorley BS': 'https://www.chorleybs.co.uk/intermediary/',
      'CoOp/Platform': 'https://www.intermediary.co-operativebank.co.uk/products/buy-to-let/',
      'Coventry': 'https://www.coventryforintermediaries.co.uk/mortgages/buy-to-let.html',
      'Coventry BS': 'https://www.coventryforintermediaries.co.uk/mortgages/buy-to-let.html',
      'Darlington': 'https://www.darlington.co.uk/darlington-intermediaries/products/',
      'Dudley': 'https://www.dudleybuildingsociety.co.uk/intermediaries/intermediaries/products/',
      'Family BS': 'https://intermediaries.familybuildingsociety.co.uk/products/buy-to-let',
      'Fleet': 'https://www.fleetmortgages.co.uk/products/',
      'Foundation': 'https://www.foundationforintermediaries.co.uk/buy-to-let/',
      'Foundation F1': 'https://www.foundationforintermediaries.co.uk/buy-to-let/',
      'Gatehouse': 'https://gatehousebank.com/intermediaries/products/buy-to-let-product-page-intermediary',
      'Hanley': 'https://www.thehanley.co.uk/intermediaries/products',
      'Harpenden': 'https://www.harpendenbs.co.uk/intermediaries-mortgage-range/',
      'HSBC': 'https://intermediaries.hsbc.co.uk/products/',
      'Interbay': 'https://www.interbay.co.uk/mortgages/buy-to-let-and-hmo/rates',
      'Kensington Prime': 'https://www.kensingtonmortgages.co.uk/intermediaries/btl-prime',
      'Kent': 'https://www.kentrelianceforintermediaries.co.uk/buy-to-let/mortgages/products',
      'Kent Reliance': 'https://www.kentrelianceforintermediaries.co.uk/buy-to-let/mortgages/products',
      'Keystone': 'https://www.keystonepropertyfinance.co.uk/broker/products-criteria/products?product=standard',
      'Landbay': 'https://landbay.co.uk/intermediaries/products-and-btl-calculator/',
      'Leeds BS': 'https://www.leedsbuildingsociety.co.uk/intermediaries/products/buy-to-let-mortgages/',
      'Leeds BS - Not for FTL': 'https://www.leedsbuildingsociety.co.uk/intermediaries/products/buy-to-let-mortgages/',
      'Leek BS': 'https://www.leekbs.co.uk/intermediaries/mortgage-products/',
      'Lendco': 'https://lendco.co.uk/broker/',
      'Lendinvest': 'https://www.lendinvest.com/intermediaries/products/buy-to-let/',
      'LendInvest - Tier 1': 'https://www.lendinvest.com/intermediaries/products/buy-to-let/',
      'Loughborough': 'https://www.theloughborough.co.uk/intermediaries/products-for-intermediaries',
      'Mansfield BS': 'https://www.mansfieldbs.co.uk/intermediaries/mortgages/',
      'Melton': 'https://www.themeltonbrokers.co.uk/',
      'Metro': 'https://www.metrobankonline.co.uk/intermediaries/buy-to-let-mortgage/',
      'Moda': 'https://modamortgages.co.uk/intermediaries/products',
      'Molo': 'https://media.molofinance.com/documents/product_guide.pdf',
      'Monmouthshire': 'https://www.mbs-intermediaries.com/mortgage-products/',
      'NatWest': 'https://www.intermediary.natwest.com/intermediary-solutions/products.html',
      'Newbury': 'https://www.newbury.co.uk/intermediaries/products/',
      'Newbury BS': 'https://www.newbury.co.uk/intermediaries/products/',
      'Newcastle': 'https://www.newcastleis.co.uk/products.aspx',
      'Nottingham': 'https://www.thenottingham-intermediaries.com/',
      'Paragon': 'https://www.paragonbank.co.uk/intermediary/mortgages/buy-to-let#product-guides',
      'Penrith BS': 'https://www.penrithbs.co.uk/mortgage/finance-a-buy-to-let-property/',
      'Pepper A-C EPC': 'https://www.pepper.money/broker/buy-to-let-products/buy-to-let-mortgage-products/',
      'Pepper A-D EPC': 'https://www.pepper.money/broker/buy-to-let-products/buy-to-let-mortgage-products/',
      'Pepper A-E EPC': 'https://www.pepper.money/broker/buy-to-let-products/buy-to-let-mortgage-products/',
      'Pepper 48 Light': 'https://www.pepper.money/broker/buy-to-let-products/buy-to-let-mortgage-products/',
      'Precise': 'https://www.precisemortgages.co.uk/intermediaries/products/buy-to-let/product-guide',
      'Principality': 'https://www.principality.co.uk/intermediaries/mortgage-products',
      'Quantum': 'https://quantummortgages.co.uk/products/',
      'Saffron': 'https://www.saffronforintermediaries.co.uk/',
      'Saffron BS': 'https://www.saffronforintermediaries.co.uk/',
      'Santander': 'https://www.santanderforintermediaries.co.uk/products-and-criteria/latest-mortgage-rates/',
      'Shawbrook': 'https://property.shawbrook.co.uk/property-finance/our-markets/buy-to-let-mortgages/',
      'Skipton': 'https://www.skipton-intermediaries.co.uk/Products',
      'SBI': 'https://sbiuk.statebank/btl-literature',
      'State Bank of India': 'https://sbiuk.statebank/btl-literature',
      'Stafford Railway': 'https://srbs.co.uk/intermediaries',
      'Suffolk': 'https://www.suffolkforintermediaries.co.uk/products/product-finder/',
      'Swansea BS': 'https://www.swansea-bs.co.uk/broker-zone/mortgage-products',
      'Teachers BS': 'https://www.teachersbuildingsociety.co.uk/intermediaries/standard-mortgages/holiday-let-buy-to-let',
      'Tipton BS': 'https://www.thetipton.co.uk/intermediaries/mortgage-product-guides/',
      'TML': 'https://themortgagelender.com/buy-to-let/product-range',
      'TMW': 'https://www.themortgageworks.co.uk/intermediaries/products/product-finder',
      'The Mortgage Works': 'https://www.themortgageworks.co.uk/intermediaries/products/product-finder',
      'TSB': 'https://intermediary.tsb.co.uk/mortgages/',
      'UTB': 'https://www.utbank.co.uk/intermediaries/mortgages/document-library/',
      'Vernon': 'https://www.thevernon.co.uk/intermediaries/products/',
      'Vida': 'https://www.vidahomeloans.co.uk/intermediaries/buy-to-let-products#section-1',
      'Vida 36': 'https://www.vidahomeloans.co.uk/intermediaries/buy-to-let-products#section-1',
      'Vida 36 - Min £150k Loan': 'https://www.vidahomeloans.co.uk/intermediaries/buy-to-let-products#section-1',
      'Virgin': 'https://intermediaries.virginmoney.com/virgin/',
      'West One': 'https://www.westoneloans.co.uk/buy-to-let-mortgages#product-guide',
      'West One - W1': 'https://www.westoneloans.co.uk/buy-to-let-mortgages#product-guide',
      'West One - W1 Portfolio': 'https://www.westoneloans.co.uk/buy-to-let-mortgages#product-guide',
      'Zephyr': 'https://zephyrhomeloans.co.uk/product-criteria/',
      'Hinckley & Rugby': 'https://intermediaries.hrbs.co.uk/product-category/buy-to-let-products/'
    };

    if (lenderURLs[lenderName]) {
      return lenderURLs[lenderName];
    }

    const partialMatches = [
      { includes: 'HSBC', url: lenderURLs['HSBC'] },
      { includes: 'BM Solutions', url: lenderURLs['BM Solutions'] },
      { includes: 'Accord', url: lenderURLs['Accord'] },
      { includes: 'Aldermore', url: lenderURLs['Aldermore'] },
      { includes: 'Bath BS', url: lenderURLs['Bath BS'] },
      { includes: 'Coventry', url: lenderURLs['Coventry BS'] },
      { includes: 'Family BS', url: lenderURLs['Family BS'] },
      { includes: 'Foundation', url: lenderURLs['Foundation'] },
      { includes: 'Gatehouse', url: lenderURLs['Gatehouse'] },
      { includes: 'Interbay', url: lenderURLs['Interbay'] },
      { includes: 'Kensington', url: lenderURLs['Kensington Prime'] },
      { includes: 'Kent', url: lenderURLs['Kent Reliance'] },
      { includes: 'Keystone', url: lenderURLs['Keystone'] },
      { includes: 'Landbay', url: lenderURLs['Landbay'] },
      { includes: 'Leeds BS', url: lenderURLs['Leeds BS'] },
      { includes: 'LendInvest', url: lenderURLs['LendInvest - Tier 1'] },
      { includes: 'Lendinvest', url: lenderURLs['LendInvest - Tier 1'] },
      { includes: 'Metro', url: lenderURLs['Metro'] },
      { includes: 'Moda', url: lenderURLs['Moda'] },
      { includes: 'Molo', url: lenderURLs['Molo'] },
      { includes: 'NatWest', url: lenderURLs['NatWest'] },
      { includes: 'Paragon', url: lenderURLs['Paragon'] },
      { includes: 'Pepper', url: lenderURLs['Pepper A-C EPC'] },
      { includes: 'Precise', url: lenderURLs['Precise'] },
      { includes: 'Quantum', url: lenderURLs['Quantum'] },
      { includes: 'Santander', url: lenderURLs['Santander'] },
      { includes: 'Shawbrook', url: lenderURLs['Shawbrook'] },
      { includes: 'Skipton', url: lenderURLs['Skipton'] },
      { includes: 'State Bank', url: lenderURLs['State Bank of India'] },
      { includes: 'SBI', url: lenderURLs['State Bank of India'] },
      { includes: 'Stafford Railway', url: lenderURLs['Stafford Railway'] },
      { includes: 'Vida', url: lenderURLs['Vida'] },
      { includes: 'Virgin', url: lenderURLs['Virgin'] },
      { includes: 'West One', url: lenderURLs['West One'] },
      { includes: 'Zephyr', url: lenderURLs['Zephyr'] }
    ];

    for (const match of partialMatches) {
      if (lenderName?.includes(match.includes)) {
        return match.url;
      }
    }

    return null;
  };

  const shouldIncludeProduct = (selectedProductType, rowProductType) => {
    const selectedLower = selectedProductType?.toLowerCase() || '';
    const rowLower = rowProductType?.toLowerCase() || '';
    
    if (rowLower === selectedLower) {
      return true;
    }
    
    if (!selectedLower.includes('green') && rowLower === selectedLower + ' green') {
      return true;
    }
    
    return false;
  };

  const getHeaderCellStyle = (columnKey) => {
    const baseStyle = {
      textAlign: 'center',
      position: 'relative',
      fontSize: '12px'
    };

    if (sortConfig.key === columnKey) {
      return {
        ...baseStyle,
        backgroundColor: '#ffffff',
        color: '#157DEC'
      };
    }

    return baseStyle;
  };

  const getDataCellStyle = (columnKey, existingClassName = '') => {
    if (columnKey === 'affordability' || columnKey === 'stressAdjustedLoan') {
      if (columnKey === 'totalCostWithFeeInterest') {
        return { fontSize: '18px' };
      }
      return {};
    }
    
    if (sortConfig.key === columnKey) {
      return {
        backgroundColor: '#ffffff',
        color: '#157DEC',
        fontSize: '18px'
      };
    }

    if (columnKey === 'totalCostWithFeeInterest' || columnKey === 'monthlyCost' || columnKey === 'monthlyCostUpfront') {
      return { fontSize: '18px' };
    }

    return {};
  };

  const handleSort = (key) => {
    let direction = 'asc';
    
    if (key === 'stressAdjustedLoan') {
      direction = 'desc';
      if (sortConfig.key === key && sortConfig.direction === 'desc') {
        direction = 'asc';
      }
    } else {
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const handleLenderToggle = (lender) => {
    setTempSelectedLenders(prev => {
      if (prev.includes(lender)) {
        return prev.filter(l => l !== lender);
      } else {
        return [...prev, lender];
      }
    });
  };

  const handleSelectAllLenders = () => {
    if (tempSelectedLenders.length === uniqueLenders.length) {
      setTempSelectedLenders([]);
    } else {
      setTempSelectedLenders([...uniqueLenders]);
    }
  };

  const handleOpenLenderModal = () => {
    if (selectedLenders.length === 0) {
      setTempSelectedLenders([...uniqueLenders]);
    } else {
      setTempSelectedLenders([...selectedLenders]);
    }
    setIsLenderModalOpen(true);
  };

  const handleDone = () => {
    setSelectedLenders([...tempSelectedLenders]);
    setIsLenderModalOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedLenders([...selectedLenders]);
    setIsLenderModalOpen(false);
  };

  const handleToggleAffordableProducts = () => {
    setShowAffordableOnly(prev => !prev);
  };

  const handleToggleGreenProducts = () => {
    setExcludeGreenProducts(prev => !prev);
  };

  const sortedResults = React.useMemo(() => {
    let sortableResults = [...results];
    
    if (selectedLenders.length > 0) {
      sortableResults = sortableResults.filter(row => selectedLenders.includes(row.lender));
    }

    if (excludeGreenProducts) {
      sortableResults = sortableResults.filter(row => !row.productType.includes('Green'));
    }

    if (showAffordableOnly) {
      sortableResults = sortableResults.filter(row => {
        const rentalCheck = row.affordability <= cleanNumber(formData.rentalIncome);
        const loanDifference = cleanNumber(formData.loanAmount) - row.stressAdjustedLoan;
        const loanCheck = loanDifference <= 0.50;
        
        return rentalCheck && loanCheck;
      });
    }
    
    if (sortConfig.key) {
      sortableResults.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          if (sortConfig.direction === 'asc') {
            return aValue - bValue;
          }
          return bValue - aValue;
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableResults;
  }, [results, sortConfig, selectedLenders, excludeGreenProducts, showAffordableOnly, formData]);

  const uniqueLenders = React.useMemo(() => {
    if (results.length === 0) return [];
    const lenders = [...new Set(results.map(row => row.lender).filter(lender => lender))];
    return lenders.sort();
  }, [results]);

  const noErcLenders = React.useMemo(() => {
    return uniqueLenders.filter(lender => 
      lender.toLowerCase().includes('no erc')
    );
  }, [uniqueLenders]);

  const handleNoErcToggle = () => {
    if (noErcLenders.every(lender => tempSelectedLenders.includes(lender))) {
      setTempSelectedLenders(prev => prev.filter(lender => !noErcLenders.includes(lender)));
    } else {
      const newSelection = [...new Set([...tempSelectedLenders, ...noErcLenders])];
      setTempSelectedLenders(newSelection);
    }
  };

  useEffect(() => {
    if (!searchCompleted) return;

    const blueHeader = document.querySelector('.sticky-header-container');
    const tableHeaders = document.querySelector('.results-table thead');
    
    if (!blueHeader || !tableHeaders) return;
    
    const blueHeaderHeight = blueHeader.offsetHeight;
    
    tableHeaders.style.position = 'sticky';
    tableHeaders.style.top = `${blueHeaderHeight}px`;
    tableHeaders.style.zIndex = '99';
    tableHeaders.style.backgroundColor = '#ffffff';
    tableHeaders.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';

    return () => {
      if (tableHeaders) {
        tableHeaders.style.position = '';
        tableHeaders.style.top = '';
        tableHeaders.style.zIndex = '';
        tableHeaders.style.backgroundColor = '';
        tableHeaders.style.boxShadow = '';
      }
    };
  }, [searchCompleted]);

  useEffect(() => {
    setResults([]);
    setSearchCompleted(false);
    setSelectedLenders([]);
    setExcludeGreenProducts(false);
    setShowAffordableOnly(false);
    setSortConfig({ key: 'totalCostWithoutFeeInterest', direction: 'asc' });

    const fetchData = async () => {
      try {
        const res = await fetch('/api/spvproducts');
        const buffer = await res.arrayBuffer();
        const workbook = read(buffer, { type: 'buffer' });
        
        const sheet = workbook.Sheets['SPV All Rates'];
        const data = utils.sheet_to_json(sheet, { header: 1 });

        const rows = data.slice(5);
        const userLTV = (cleanNumber(formData.loanAmount) / cleanNumber(formData.propertyValue));

        const portfolioLandlordLenders = [
          'Aldermore', 'Bath BS', 'BM Solutions', 'Cambridge', 'Castle Trust', 'CHL 1', 'Chorley BS', 'Coventry BS', 'Family BS', 'Fleet', 'Foundation F1', 
          'Gatehouse', 'Hinckley & Rugby', 'Interbay', 'Kent Reliance', 'Kensington Prime', 'Keystone', 
          'Landbay', 'Leeds BS', 'Lendco', 'LendInvest - Tier 1', 'Mansfield BS', 'Leeds BS - Not for FTL', 
          'Pepper A-C EPC', 'Metro', 'Moda', 'Molo', 'Monmouthshire', 'Newbury BS', 'Nottingham', 'Paragon', 
          'Pepper A-E EPC', 'Precise', 'Quantum', 'Saffron BS', 'State Bank of India', 'Shawbrook', 'TML', 
          'The Mortgage Works', 'UTB', 'Vernon', 'Vida 36', 'West One - W1 Portfolio', 'Zephyr', 'Pepper A-D EPC', 'Vida 36 - Min £150k Loan'
        ];

        const filtered = rows
          .filter(row => {
            const payrate = parseFloat(row[8]);
            return payrate > 0;
          })
          .filter(row => {
            return row[2] === formData.transactionType;
          })
          .filter(row => {
            return shouldIncludeProduct(formData.productType, row[3]);
          })
          .filter(row => {
  const lender = row[1];
  let pass = true;
  
  if (lender === 'West One - W1 Portfolio') {
    pass = formData.landlordType === 'Portfolio Landlord';
  } else if (lender === 'West One - W1') {
    pass = formData.landlordType !== 'Portfolio Landlord';
  } else if (formData.landlordType === 'Portfolio Landlord') {
    pass = portfolioLandlordLenders.some(portfolioLender => lender?.includes(portfolioLender));
  }
  
  return pass;
})
          .filter(row => {
            if (formData.landlordType === 'Portfolio Landlord') {
              const lender = row[1];
              const maxLTV = parseFloat(row[5]);
              const productFeeBase = parseFloat(row[9]);
              
              if (lender === 'Precise' && 
                  (maxLTV === 65 || maxLTV === 0.65 || maxLTV === 75 || maxLTV === 0.75) && 
                  (productFeeBase === 5 || productFeeBase === 0.05)) {
                return false;
              }
            }
            return true;
          })
          .filter(row => {
            const maxLTV = parseFloat(row[5]);
            const userLTVPercent = userLTV * 100;
            
            if (maxLTV <= 1) {
              return userLTV <= maxLTV;
            } else {
              return userLTVPercent <= maxLTV;
            }
          })
          .filter(row => {
            const requiredLoanAmount = cleanNumber(formData.loanAmount);
            const minLoanAmount = parseFloat(row[19]) || 0;
            let maxLoanAmount = parseFloat(row[18]) || Infinity;
            
            // TMW override
            if ((row[1] === 'TMW' || row[1] === 'The Mortgage Works') && userLTV <= 0.50) {
              maxLoanAmount = 1500000;
            }
            
            // Bank of Ireland override
            if (row[1] === 'Bank of Ireland' || row[1].includes('Bank of Ireland')) {
              // For pound for pound remortgages: minimum loan is £100,000
              if (formData.transactionType === 'Remo £4£' && requiredLoanAmount < 100000) {
                return false; // Exclude if pound for pound remortgage and loan amount is less than £100,000
              }
            }

            // BM Solutions override
            if ((row[1] === 'BM Solutions' || row[1].includes('BM Solutions')) && userLTV <= 0.65) {
              maxLoanAmount = 2000000;
            }
            
            // Coventry override
            if ((row[1] === 'Coventry' || row[1] === 'Coventry BS') && row[1].includes('Coventry')) {
              if (userLTV <= 0.50) {
                maxLoanAmount = 1000000;
              } else if (userLTV >= 0.5001 && userLTV <= 0.75) {
                maxLoanAmount = 750000;
              }
            }
            
            // Bath BS override
            if ((row[1] === 'Bath BS' || row[1].includes('Bath BS')) && userLTV <= 0.75) {
              maxLoanAmount = 1500000;
            }
            
            // Fleet override
            if ((row[1] === 'Fleet' || row[1].includes('Fleet'))) {
              if (userLTV <= 0.65) {
                maxLoanAmount = 2000000;
              } else if (userLTV > 0.65 && userLTV <= 0.70) {
                maxLoanAmount = 1500000;
              }
            }
            
            // Quantum override
            if ((row[1] === 'Quantum' || row[1].includes('Quantum')) && userLTV <= 0.65) {
              maxLoanAmount = 1500000;
            }
            
            // Tipton BS override
            if ((row[1] === 'Tipton BS' || row[1].includes('Tipton')) && userLTV <= 0.70) {
              maxLoanAmount = 1250000;
            }

            // Co-Op Bank override
            if (row[1] === 'CoOp/Platform' || row[1].includes('CoOp') || row[1].includes('Co-op')) {
              // Check loan amount tiers
              if (requiredLoanAmount <= 750000) {
                if (requiredLoanAmount <= 350000) {
                  // Up to £350,000: 75% LTV (or 70% if remortgage with capital raising)
                  const maxLTV = formData.transactionType === 'Remortgage' ? 0.70 : 0.75;
                  if (userLTV > maxLTV) {
                    return false;
                  }
                } else if (requiredLoanAmount >= 350001 && requiredLoanAmount <= 750000) {
                  // £350,001 to £750,000: 65% LTV (always, regardless of transaction type)
                  if (userLTV > 0.65) {
                    return false;
                  }
                }
              } else {
                // Exclude loans over £750,000
                return false;
              }
            }
            
            return requiredLoanAmount >= minLoanAmount && requiredLoanAmount <= maxLoanAmount;
          })
          .map(row => {
            const requiredLoanAmount = cleanNumber(formData.loanAmount);
            const rentalIncome = cleanNumber(formData.rentalIncome);
            const propertyValue = cleanNumber(formData.propertyValue);

            const taxRate = parseFloat(row[6]);
            const assessmentRate = parseFloat(row[12]);
            const productPayrate = parseFloat(row[8]);
            const maxLTV = parseFloat(row[5]);
            const years = parseFloat(row[4]);
            
            const cashbackAmount = parseFloat(row[20]) || 0;
            const productFeeBase = parseFloat(row[9]);
            
            let productFee;
            if (row[1] && row[1].includes('Family BS')) {
              const calculatedFee = productFeeBase < 50 ? requiredLoanAmount * productFeeBase : productFeeBase;
              
              if (productFeeBase === 0.01) {
                productFee = Math.max(calculatedFee, 1000);
              } else if (productFeeBase === 0.03) {
                productFee = Math.max(calculatedFee, 1500);
              } else {
                productFee = calculatedFee;
              }
            } else {
              productFee = productFeeBase < 50 ? requiredLoanAmount * productFeeBase : productFeeBase;
            }

                        // Initialize variables for calculations
            let monthlyCost, totalCostWithFeeInterest;

            // Apply all lender-specific fee addition rules without early returns
            if (row[1] && (row[1].includes('Aldermore') || row[1] === 'Aldermore')) {
              const loanPlusFeeRatio = (requiredLoanAmount + productFee) / propertyValue;
              if (loanPlusFeeRatio > 0.82) {
                monthlyCost = "lender fee cannot be added";
                totalCostWithFeeInterest = "lender fee cannot be added";
              }
            }

            if (row[1] && (row[1].includes('Leeds BS') || row[1] === 'Leeds BS')) {
              const loanPlusFeeRatio = (requiredLoanAmount + productFee) / propertyValue;
              if (loanPlusFeeRatio > 0.80) {
                monthlyCost = "lender fee cannot be added";
                totalCostWithFeeInterest = "lender fee cannot be added";
              }
            }

            if (row[1] && (row[1].includes('UTB') || row[1] === 'UTB')) {
              const loanPlusFeeRatio = (requiredLoanAmount + productFee) / propertyValue;
              if (loanPlusFeeRatio > 0.80) {
                monthlyCost = "lender fee cannot be added";
                totalCostWithFeeInterest = "lender fee cannot be added";
              }
            }

            if (row[1] && (row[1].includes('Gatehouse') || row[1] === 'Gatehouse')) {
              productFee = Math.max(productFee, 999);
              const loanPlusFeeRatio = (requiredLoanAmount + productFee) / propertyValue;
              if (loanPlusFeeRatio > maxLTV) {
                monthlyCost = "lender fee cannot be added";
                totalCostWithFeeInterest = "lender fee cannot be added";
              }
            }
            if (row[1] && (row[1].includes('Nottingham') || row[1] === 'Nottingham')) {
  const loanPlusFeeRatio = (requiredLoanAmount + productFee) / propertyValue;
  if (loanPlusFeeRatio > 0.80) {
    monthlyCost = "lender fee cannot be added";
    totalCostWithFeeInterest = "lender fee cannot be added";
  }
}
if (row[1] && (row[1].includes('Dudley') || row[1] === 'Dudley BS' || row[1] === 'Dudley')) {
  const loanPlusFeeRatio = (requiredLoanAmount + productFee) / propertyValue;
  if (loanPlusFeeRatio > maxLTV) {
    monthlyCost = "lender fee cannot be added";
    totalCostWithFeeInterest = "lender fee cannot be added";
  }
}
if (row[1] && (row[1].includes('Foundation') || row[1] === 'Foundation' || row[1] === 'Foundation F1')) {
  const loanPlusFeeLower = requiredLoanAmount + productFee;
  const maxLoanAmount = parseFloat(row[18]) || Infinity; // Adjust column if needed
  if (loanPlusFeeLower > maxLoanAmount) {
    monthlyCost = "lender fee cannot be added";
    totalCostWithFeeInterest = "lender fee cannot be added";
  }
}
            // Default calculations (only if not overridden)
            if (monthlyCost === undefined) {
              monthlyCost = ((requiredLoanAmount + productFee) * productPayrate) / 12;
            }
            if (totalCostWithFeeInterest === undefined) {
totalCostWithFeeInterest = productFee + ((requiredLoanAmount * productPayrate) * years) + ((productFee * productPayrate) * years) - cashbackAmount;            }

            const monthlyCostUpfront = (requiredLoanAmount * productPayrate) / 12;
const totalCostWithoutFeeInterest = productFee + ((requiredLoanAmount * productPayrate) * years) - cashbackAmount;
            
            const affordability = ((requiredLoanAmount * assessmentRate) * taxRate) / 12;

            const stressTestLoan = ((rentalIncome / taxRate) / assessmentRate) * 12;
            const maxLTVLoan = propertyValue * maxLTV;
            let stressAdjustedLoan = Math.min(stressTestLoan, maxLTVLoan);

            // Apply Remo £4£ cap - maximum borrowing cannot exceed required loan amount for remortgages
            if (formData.transactionType === "Remo £4£" && formData.loanAmount) {
              const requiredLoanAmount = parseFloat(formData.loanAmount.toString().replace(/,/g, '')) || 0;
              
              if (requiredLoanAmount > 0) {
                stressAdjustedLoan = Math.min(stressAdjustedLoan, requiredLoanAmount);
              }
            }

            return {
              lender: row[1] === 'West One - W1 Portfolio' ? 'West One - W1' : row[1],
              productType: row[3],
              productPayrate,
              productFee,
              maxLtv: row[5],
              monthlyCostUpfront,
              monthlyCost,
              totalCostWithoutFeeInterest,
              totalCostWithFeeInterest,
              affordability,
              stressAdjustedLoan,
              lenderMaxLoan: row[18],
            };
          });

        setResults(filtered);
        setSearchCompleted(true);
      } catch (err) {
        console.error('Error reading Excel file:', err);
        setSearchCompleted(true);
      }
    };

    if (formData) {
      fetchData();
    }
  }, [formData, location.pathname]);

  return (
    <div className="results-container results-page">
      <div className="sticky-header-container" style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{
          backgroundColor: '#157DEC',
          color: 'white',
          fontFamily: 'Century Gothic, sans-serif',
          fontWeight: 'bold',
          fontSize: '24px',
          textAlign: 'center',
          padding: '20px 20px 5px 20px',
          position: 'relative',
          left: '-2rem',
          right: '-2rem',
          width: '100vw',
          maxWidth: 'none',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          marginTop: '-2rem',
          marginBottom: '0.5rem'
        }}>
          {formData?.landlordType === 'Portfolio Landlord' 
            ? 'LIMITED COMPANY (SPV) - PORTFOLIO LANDLORD' 
            : 'LIMITED COMPANY (SPV) - NON PORTFOLIO LANDLORD'}
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          backgroundColor: '#157DEC', 
          padding: '5px 20px 20px 20px', 
          borderRadius: '0 0 10px 10px', 
          marginTop: '0', 
          marginBottom: '0px',
          position: 'relative',
          left: '-2rem',
          right: '-2rem',
          width: '100vw',
          maxWidth: 'none',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', height: '100%' }}>
            <div className="summary-panel" style={{ margin: '0' }}>
              <div className="summary-row" style={{ flexWrap: 'nowrap', margin: '0' }}>
                <div className="summary-item">
                  <label>Transaction Type</label>
                  <div>{formData?.transactionType}</div>
                </div>
                <div className="summary-item">
                  <label>Monthly Rental Income</label>
                  <div>£{Number(formData?.rentalIncome).toLocaleString()}</div>
                </div>
                <div className="summary-item">
                  <label>Required Loan Amount</label>
                  <div>£{Number(formData?.loanAmount).toLocaleString()}</div>
                </div>
                <div className="summary-item">
                  <label>Property Value</label>
                  <div>£{Number(formData?.propertyValue).toLocaleString()}</div>
                </div>
                <div className="summary-item">
                  <label>Resultant LTV</label>
                  <div>{((cleanNumber(formData?.loanAmount) / cleanNumber(formData?.propertyValue)) * 100).toFixed(2)}%</div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ height: '10px' }}></div>
              <button
                className="back-button"
                style={{ backgroundColor: 'white', color: '#157DEC', padding: '8px 14px', borderRadius: '15px', border: '1px solid #157DEC', fontWeight: 'bold', textAlign: 'center' }}
                onClick={() => navigate('/spv', { state: { formData } })}
              >
                ← BACK
              </button>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: '#157DEC',
          height: '0px',
          marginLeft: '-2rem',
          marginRight: '-2rem',
          marginTop: '0',
          marginBottom: '0'
        }}></div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          marginTop: '8px',
          marginBottom: '8px',
          paddingRight: '0px'
        }}>
          <div 
            onClick={handleToggleAffordableProducts}
            style={{
              width: '20%',
              height: '30px',
              backgroundColor: showAffordableOnly ? 'white' : '#00B050',
              color: showAffordableOnly ? '#157DEC' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Century Gothic, sans-serif',
              fontWeight: 'bold',
              fontSize: '12px',
              borderRadius: '5px',
              border: '3px solid #000000',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            {showAffordableOnly ? 'Show All Products' : 'Show Affordable Products Only'}
          </div>
        </div>
      </div>

      {isLenderModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: '#f5f7fa',
            border: '3px solid #157DEC',
            borderRadius: '10px',
            padding: '20px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Century Gothic, sans-serif'
          }}>
            <div style={{
              backgroundColor: '#157DEC',
              color: 'white',
              padding: '15px',
              margin: '-20px -20px 20px -20px',
              borderRadius: '7px 7px 0 0',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              Select Lenders ({uniqueLenders.length} available)
            </div>

            <div style={{
              backgroundColor: '#157DEC',
              color: 'white',
              padding: '10px 15px',
              borderRadius: '5px',
              marginBottom: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
            onClick={handleSelectAllLenders}>
              <span>Select All Lenders</span>
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: 'white',
                border: '2px solid white',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                color: '#157DEC',
                fontWeight: 'bold'
              }}>
                {tempSelectedLenders.length === uniqueLenders.length && uniqueLenders.length > 0 ? '✓' : ''}
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              border: '2px solid #157DEC',
              borderRadius: '5px',
              maxHeight: '400px',
              overflowY: 'auto',
              marginBottom: '20px'
            }}>
              {/* No ERC Lenders Option - at the top */}
              {noErcLenders.length > 0 && (
                <div
                  style={{
                    padding: '12px 15px',
                    borderBottom: '2px solid #157DEC',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: noErcLenders.every(lender => tempSelectedLenders.includes(lender)) ? '#e3f2fd' : '#f8f9fa',
                    fontWeight: 'bold'
                  }}
                  onClick={handleNoErcToggle}
                >
                  <span style={{ 
                    color: '#157DEC', 
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    Lenders with No ERC's ({noErcLenders.length})
                  </span>
                  <input
                    type="checkbox"
                    checked={noErcLenders.every(lender => tempSelectedLenders.includes(lender)) && noErcLenders.length > 0}
                    onChange={() => {}}
                    style={{ transform: 'scale(1.2)' }}
                  />
                </div>
              )}
              {uniqueLenders.map((lender, index) => (
                <div
                  key={lender}
                  style={{
                    padding: '12px 15px',
                    borderBottom: index < uniqueLenders.length - 1 ? '1px solid #e0e0e0' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: tempSelectedLenders.includes(lender) ? '#e3f2fd' : 'white'
                  }}
                  onClick={() => handleLenderToggle(lender)}
                >
                  <span style={{ 
                    color: '#333', 
                    fontSize: '14px',
                    fontWeight: tempSelectedLenders.includes(lender) ? 'bold' : 'normal'
                  }}>
                    {lender}
                  </span>
                  <input
                    type="checkbox"
                    checked={tempSelectedLenders.includes(lender)}
                    onChange={() => {}}
                    style={{ transform: 'scale(1.2)' }}
                  />
                </div>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCancel}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                CANCEL
              </button>
              <button
                onClick={handleDone}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                DONE
              </button>
            </div>
          </div>
        </div>
      )}

      {searchCompleted && sortedResults.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          backgroundColor: 'white',
          border: '3px double #000000',
          borderRadius: '8px',
          margin: '20px 0',
          fontFamily: 'Century Gothic, sans-serif'
        }}>
          <h3 style={{
            color: '#157DEC',
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '10px'
          }}>
            No Products Are Available Based on Your Current Parameters
          </h3>
          <p style={{
            color: '#157DEC',
            fontSize: '14px',
            margin: '0'
          }}>
            Please Change the Client Requirements and Try Again
          </p>
        </div>
      ) : searchCompleted ? (
        <table className="results-table" style={{ marginTop: '20px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'center', position: 'relative', padding: '10px 0', borderBottom: 'none', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Lender
                </div>
              </th>
              <th style={{ textAlign: 'center', position: 'relative', padding: '10px 0', borderBottom: 'none', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Product Type
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('productPayrate'), padding: '10px 0', borderBottom: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Product Payrate
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('productFee'), padding: '10px 0', borderBottom: 'none', width: '10%' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Lender Product Fee
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('monthlyCostUpfront'), padding: '10px 0', borderBottom: 'none', width: '10%' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Interest Only Monthly Payment with Product Fee Paid Upfront
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('totalCostWithoutFeeInterest'), padding: '10px 0', borderBottom: 'none', width: '10%' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Approx. TAP During Promo Period - Fee Paid Upfront*
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('monthlyCost'), padding: '10px 0', borderBottom: 'none', width: '10%' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Interest Only Monthly Payment with Product Fee Added
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('totalCostWithFeeInterest'), padding: '10px 0', borderBottom: 'none', width: '10%' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Approx. TAP During Promo Period - Fee Added to Loan*
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('affordability'), padding: '10px 0', borderBottom: 'none', width: '10%' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Minimum Rental Income Required to Match Loan Amount
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('stressAdjustedLoan'), padding: '10px 0', borderBottom: 'none', width: '10%' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  Maximum Loan Possible Based on Stated Rental Income
                </div>
              </th>
            </tr>
            <tr style={{ borderTop: 'none' }}>
              <th style={{ textAlign: 'center', position: 'relative', padding: '5px', borderTop: 'none', borderBottom: 'none', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <button
                    onClick={handleOpenLenderModal}
                    disabled={uniqueLenders.length === 0}
                    style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      border: '3px solid #000000',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#157DEC',
                      cursor: uniqueLenders.length === 0 ? 'not-allowed' : 'pointer',
                      minWidth: '120px',
                      fontWeight: 'bold'
                    }}
                  >
                    {uniqueLenders.length === 0 
                      ? 'Loading...' 
                      : selectedLenders.length === 0 
                      ? 'Select Lenders' 
                      : `${selectedLenders.length} Selected`}
                  </button>
                </div>
              </th>
              <th style={{ textAlign: 'center', position: 'relative', padding: '5px', borderTop: 'none', borderBottom: 'none', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <button
                    onClick={handleToggleGreenProducts}
                    style={{
                      fontSize: '10px',
                      padding: '4px 6px',
                      border: '3px solid #000000',
                      borderRadius: '8px',
                      backgroundColor: '#00B050',
                      color: 'white',
                      cursor: 'pointer',
                      minWidth: '120px',
                      fontWeight: 'bold',
                      lineHeight: '1.1',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {excludeGreenProducts ? 'Include Green' : 'Exclude Green'}
                  </button>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('productPayrate'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('productPayrate')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('productPayrate')}</div>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('productFee'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('productFee')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('productFee')}</div>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('monthlyCostUpfront'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('monthlyCostUpfront')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('monthlyCostUpfront')}</div>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('totalCostWithoutFeeInterest'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('totalCostWithoutFeeInterest')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('totalCostWithoutFeeInterest')}</div>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('monthlyCost'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('monthlyCost')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('monthlyCost')}</div>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('totalCostWithFeeInterest'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('totalCostWithFeeInterest')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('totalCostWithFeeInterest')}</div>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('affordability'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('affordability')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('affordability')}</div>
                </div>
              </th>
              <th style={{ ...getHeaderCellStyle('stressAdjustedLoan'), padding: '5px', borderTop: 'none', borderBottom: 'none' }} onClick={() => handleSort('stressAdjustedLoan')}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px' }}>{getSortIcon('stressAdjustedLoan')}</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedResults.map((row, index) => {
              const loanDifference = cleanNumber(formData.loanAmount) - row.stressAdjustedLoan;
              const isLoanEligible = loanDifference <= 0.50;
              const rentalDifference = row.affordability - cleanNumber(formData.rentalIncome);
              const isRentalEligible = rentalDifference <= 0.50;

              return (
                <tr key={index} style={{ height: '30px' }}>
                  <td className="result-cell lender-cell" style={{
                    ...getDataCellStyle('lender'),
                    height: '30px',
                    verticalAlign: 'middle',
                    ...(row.productType && row.productType.includes('Green') ? {
                      backgroundColor: '#00B050',
                      color: 'white',
                      fontWeight: 'bold'
                    } : {})
                  }}>
                    {getLenderWebsiteURL(row.lender) ? (
                      <a 
                        href={getLenderWebsiteURL(row.lender)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          color: 'inherit',
                          textDecoration: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {row.lender}
                      </a>
                    ) : (
                      row.lender
                    )}
                  </td>
                  <td className="result-cell product-type-cell" style={{
                    ...getDataCellStyle('productType'),
                    height: '30px',
                    verticalAlign: 'middle',
                    ...(row.productType && row.productType.includes('Green') ? {
                      backgroundColor: '#00B050',
                      color: 'white',
                      fontWeight: 'bold'
                    } : {})
                  }}>{row.productType}</td>
                  <td className="result-cell product-payrate-cell" style={{
                    ...getDataCellStyle('productPayrate'),
                    height: '30px',
                    verticalAlign: 'middle'
                  }}>{(row.productPayrate * 100).toFixed(2)}%</td>
                  <td className="result-cell product-fee-cell" style={{
                    ...getDataCellStyle('productFee'),
                    height: '30px',
                    verticalAlign: 'middle',
                    width: '10%'
                  }}>£{row.productFee % 1 === 0 ? row.productFee.toLocaleString() : row.productFee.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}</td>
                  <td className="result-cell monthly-cost-upfront-cell monthly-cost-cell" style={{
                    ...getDataCellStyle('monthlyCostUpfront'),
                    height: '30px',
                    verticalAlign: 'middle',
                    width: '10%',
                    backgroundColor: '#157DEC',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>£{row.monthlyCostUpfront.toFixed(2)}</td>
                  <td className="result-cell total-cost-lite-cell" style={{
                    ...getDataCellStyle('totalCostWithoutFeeInterest'),
                    height: '30px',
                    verticalAlign: 'middle',
                    width: '10%',
                    backgroundColor: '#0D54A3',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '18px'
                  }}>£{Math.round(row.totalCostWithoutFeeInterest).toLocaleString()}</td>
                  <td className="result-cell monthly-cost-cell" style={{
                    height: '30px',
                    verticalAlign: 'middle',
                    backgroundColor: '#157DEC',
                    color: 'white',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    {typeof row.monthlyCost === 'string' ? (
                      <span style={{ fontSize: '10px', lineHeight: '1.2', display: 'block' }}>
                        {row.monthlyCost}
                      </span>
                    ) : (
                      <span style={{ fontSize: '18px' }}>
                        £{row.monthlyCost.toFixed(2)}
                      </span>
                    )}
                  </td>
                  <td className="result-cell total-cost-cell" style={{
                    height: '30px',
                    verticalAlign: 'middle',
                    backgroundColor: '#0D54A3',
                    color: 'white',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    {typeof row.totalCostWithFeeInterest === 'string' ? (
                      <span style={{ fontSize: '10px', lineHeight: '1.2', display: 'block' }}>
                        {row.totalCostWithFeeInterest}
                      </span>
                    ) : (
                      <span style={{ fontSize: '18px' }}>
                        £{Math.round(row.totalCostWithFeeInterest).toLocaleString()}
                      </span>
                    )}
                  </td>
                  <td className={`result-cell ${isRentalEligible ? 'affordability-cell' : 'unaffordable-cell'}`} style={{
                    ...getDataCellStyle('affordability'),
                    height: '30px',
                    verticalAlign: 'middle'
                  }}>
                    £{Math.round(row.affordability).toLocaleString()}
                  </td>
                  <td className={`result-cell ${isLoanEligible ? 'loan-eligible-cell' : 'loan-ineligible-cell'}`} style={{
                    ...getDataCellStyle('stressAdjustedLoan'),
                    height: '30px',
                    verticalAlign: 'middle'
                  }}>
                    £{Math.round(row.stressAdjustedLoan).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          fontFamily: 'Century Gothic, sans-serif',
          fontSize: '16px',
          color: '#FFFFFF'
        }}>
          Loading products...
        </div>
      )}

      <Footer />
    </div>
  );
}

export default ResultsPage;